import React, { useContext } from "react";
import { navigate } from "gatsby";
import { useSelector } from "react-redux";
import { ROUTES } from "../../../Routes";
import { CUSTOM } from "../../../constant/ReportConstants";
import { useDashboardFilter } from "../../../context/DashboardFilterContext";
import { getLocationId } from "../../../redux/slices/locationSlice";
import { UserContext } from "../../../amplify/authenticator-provider";
import { checkAccessPermission } from "../../../library/helpers";
import { AccessPermissionAction } from "../../../library/constants";
import cn from "../../../utils/cn";

/**
 * Build the Checkout Report URL pre-filtered to a user + location + the
 * dashboard's current date selection. The dashboard already stores `dateRange`
 * as ISO strings, so they go straight into the query string.
 *
 * @param {{ userId: number|string, locId?: number|string, filterType?: string, dateRange?: string[] }} params
 * @returns {string} path + query string
 */
export const buildCheckoutReportPath = ({ userId, locId, filterType, dateRange } = {}) => {
  const params = new URLSearchParams();
  // `loc` is the app-wide location param — layout.js switches the global
  // location for it, and the report pins its own filter from it.
  if (locId != null && locId !== "") params.set("loc", String(locId));
  if (userId != null && userId !== "") params.set("userId", String(userId));
  if (filterType) params.set("filterType", filterType);
  // Only a custom range carries explicit dates; presets are re-derived server-side.
  if (filterType === CUSTOM && dateRange?.[0] && dateRange?.[1]) {
    params.set("startDate", dateRange[0]);
    params.set("endDate", dateRange[1]);
  }
  const qs = params.toString();
  return qs ? `${ROUTES.dailyCheckoutReport}?${qs}` : ROUTES.dailyCheckoutReport;
};

/**
 * Base username → Checkout Report link. Fully controlled: the caller supplies
 * the date scope (`filterType` / `dateRange`) and `locId`. Use this directly in
 * reports that aren't inside a `DashboardFilterProvider` (e.g. Media Detail
 * Report). Dashboard widgets use the `CheckoutReportUserLink` wrapper below,
 * which sources those from context.
 *
 * Permission rule: the current user's OWN name always links; ANOTHER user's
 * name links only with Location-wise report permission — otherwise plain text.
 *
 * forwardRef + `rest` spread so it works as an antd `Tooltip` child (Tooltip
 * clones the child to inject a ref + hover handlers).
 *
 * @param {object} props
 * @param {number|string} [props.userId]     User to pre-filter the report by.
 * @param {string}        props.name         Display name.
 * @param {string}        [props.filterType] Date filter token.
 * @param {string[]}      [props.dateRange]  ISO [start, end] for a custom range.
 * @param {number|string} [props.locId]      Location to pin.
 * @param {string}        [props.className]  Extra classes for the text node.
 */
export const CheckoutUserLink = React.forwardRef(
  ({ userId, name, filterType, dateRange, locId, className = "", ...rest }, ref) => {
    const { user } = useContext(UserContext);
    const label = name || "";
    const isLocationWise = checkAccessPermission(
      AccessPermissionAction.REPORT_MANAGEMENT_LOCATION_WISE_VIEW
    );
    const isOwnName = user?.id != null && Number(userId) === Number(user.id);
    const hasTarget = userId != null && userId !== "" && !!label && (isOwnName || isLocationWise);

    const go = e => {
      // Rows/cards may have their own click handlers (drawers) — don't trigger
      // both when the name itself is clicked.
      e.stopPropagation();
      navigate(buildCheckoutReportPath({ userId, locId, filterType, dateRange }));
    };

    if (!hasTarget) {
      return (
        <span ref={ref} className={className} {...rest}>
          {label}
        </span>
      );
    }

    return (
      <span
        ref={ref}
        role="link"
        tabIndex={0}
        {...rest}
        className={cn("cursor-pointer text-twLinkColor", className)}
        onClick={go}
        onKeyDown={e => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          go(e);
        }}
      >
        {label}
      </span>
    );
  }
);

CheckoutUserLink.displayName = "CheckoutUserLink";

/**
 * Dashboard wrapper — sources the date scope from `DashboardFilterContext` and
 * the location from the session, so widget call sites only pass the user. Every
 * widget that renders this is inside a `DashboardFilterProvider` (the dashboard
 * itself, and CommunicationDetailReport which mounts its own), so the hook
 * always resolves.
 */
const CheckoutReportUserLink = React.forwardRef((props, ref) => {
  const { filterType, dateRange } = useDashboardFilter();
  const sessionLocationId = useSelector(getLocationId);
  // Only pin a single concrete location — "all locations" is a comma-joined
  // list, which the report can't take, so we leave it to the session default.
  const locId =
    sessionLocationId != null && !String(sessionLocationId).includes(",")
      ? sessionLocationId
      : null;
  return (
    <CheckoutUserLink
      ref={ref}
      filterType={filterType}
      dateRange={dateRange}
      locId={locId}
      {...props}
    />
  );
});

CheckoutReportUserLink.displayName = "CheckoutReportUserLink";

export default CheckoutReportUserLink;
